import { Component, OnInit, OnDestroy, AfterViewInit, Inject, PLATFORM_ID, ChangeDetectorRef } from "@angular/core";
import { CommonModule, isPlatformBrowser } from "@angular/common";
import { Router } from "@angular/router";
import { finalize } from "rxjs/operators";
import { ActivitySelectionService, ActivityCategory, Activity } from "../services/activity-selection.service";
import { ActivityService, ActivityApiItem } from "../core/services/activity.service";
import { generateDateOptions, getImageUrl } from "../shared/utils/commenFunctions";
import { FormDateUtils } from "../shared/utils/form-date-utils";
import { NiButtonComponent } from "../shared/lib/ni-button/ni-button.component";
import { NiAlertComponent } from "../shared/lib/ni-alert/ni-alert.component";
import { pageLoadAnimation } from "../shared/utils/common-animations";
import { getUserDetails } from "../shared/utils/storage-utils";
import { ImagePreloadService } from "../shared/services/image-preload.service";

type ActivityGroupTab = {
  id: ActivityCategory;
  label: string;
};

@Component({
  selector: "activity-screen-component, ActivityScreenComponent",
  standalone: true,
  templateUrl: './activity-screen.component.html',
  styleUrls: ['./activity-screen.component.css'],
  imports: [CommonModule, NiButtonComponent, NiAlertComponent],
  animations: [pageLoadAnimation]
})
export class ActivityScreenComponent implements OnInit, OnDestroy, AfterViewInit {
  selectedCategory: ActivityCategory = '';
  isLoading = false;
  error: string | null = null;
  isSaving = false;
  userDetails: { value?: string | number } = getUserDetails();
  toasterMessage: string | null = null;
  toasterVariant: "success" | "danger" | "warning" | "info" = "info";
  private toasterTimeoutId: ReturnType<typeof setTimeout> | null = null;
  pageLoadState = 'hidden';

  /** Built dynamically from API activity type / groupName — not hardcoded. */
  categories: ActivityGroupTab[] = [];

  allActivities: Activity[] = [];
  imagesLoaded: { [key: string]: boolean } = {};
  private readonly imageUrlCache = new Map<string, string>();
  preloadedImageUrls = new Map<string, string>();
  // Store API's isSubmitted status - this is the source of truth
  private readonly activitySubmittedStatus = new Map<string, boolean>();
  // Temporary selection state for current session (not persisted)
  // Tracks activities that are temporarily selected (not yet saved to API)
  private readonly temporarySelections = new Set<string>();
  // Tracks activities that are temporarily deselected (were in API but user deselected)
  private readonly temporaryDeselections = new Set<string>();

  constructor(
    private readonly router: Router,
    private readonly activitySelectionService: ActivitySelectionService,
    private readonly activityService: ActivityService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly cdr: ChangeDetectorRef,
    private readonly imagePreloadService: ImagePreloadService
  ) {}

  ngOnDestroy(): void {
    if (this.toasterTimeoutId !== null) {
      clearTimeout(this.toasterTimeoutId);
    }
  }

  ngOnInit(): void {
    this.temporarySelections.clear();
    this.temporaryDeselections.clear();
    this.loadActivities();
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.pageLoadState = 'visible';
      }, 50);
    });
  }

  /** Calendar day for this activity booking (from edit/plan flow, else today). */
  private resolveActivityDate(): string {
    const stored = (sessionStorage.getItem('dateValue') || '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(stored)) {
      return stored;
    }
    return generateDateOptions(1)[0]?.dateValue ?? this.getTodayIsoDate();
  }

  private getTodayIsoDate(): string {
    return FormDateUtils.formatHoustonIsoDate();
  }

  loadActivities(): void {
    this.isLoading = true;
    this.error = null;

    // Do not pass group_name — load all activities and group by type client-side.
    const payload: Record<string, unknown> = {
      index: 0,
      limit: 100,
      date: this.resolveActivityDate(),
    };
    const userId = Number(this.userDetails?.value);
    if (Number.isFinite(userId) && userId > 0) {
      payload['userid'] = userId;
    }

    const minLoadingTime = 800;
    const startTime = Date.now();

    this.activityService.getActivities(payload).subscribe({
      next: (response) => {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

        setTimeout(() => {
          if (response.statusCode === 200) {
            // Special activities are Events only — never show on Activity Screen.
            const regularActivities = (response.activities ?? []).filter(
              (activity) => activity.isSpecial !== true,
            );
            if (regularActivities.length > 0) {
              this.activitySubmittedStatus.clear();

              this.allActivities = regularActivities.map((activity) => {
                const isSubmitted =
                  (activity as ActivityApiItem & { isSubmitted?: boolean }).isSubmitted === true;
                this.activitySubmittedStatus.set(activity.id, isSubmitted);

                const rawImage = (activity.activityImage ?? '').trim();
                return {
                  id: activity.id,
                  name: activity.activityName,
                  category: this.resolveActivityGroup(activity),
                  image: rawImage ? getImageUrl(rawImage) : '',
                };
              });

              this.rebuildCategoriesFromActivities();

              if (isPlatformBrowser(this.platformId)) {
                this.preloadActivityImages();
              }
            } else {
              this.allActivities = [];
              this.categories = [];
              this.selectedCategory = '';
              this.activitySubmittedStatus.clear();
            }
          } else {
            this.error = response.message ?? 'Failed to load activities';
          }
          this.isLoading = false;
        }, remainingTime);
      },
      error: () => {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

        setTimeout(() => {
          this.error = 'Failed to load activities. Please try again.';
          this.allActivities = [];
          this.categories = [];
          this.selectedCategory = '';
          this.isLoading = false;
        }, remainingTime);
      },
    });
  }

  private resolveActivityGroup(activity: ActivityApiItem): string {
    const raw =
      (activity.type ?? '').trim() ||
      (activity.groupName ?? '').trim() ||
      'Other';
    return raw;
  }

  private rebuildCategoriesFromActivities(): void {
    const seen = new Map<string, string>();

    for (const activity of this.allActivities) {
      const label = (activity.category || 'Other').trim() || 'Other';
      const id = this.toCategoryId(label);
      if (!seen.has(id)) {
        seen.set(id, label);
      }
      // Normalize activity.category to stable id used for filtering
      activity.category = id;
    }

    this.categories = Array.from(seen.entries()).map(([id, label]) => ({ id, label }));

    if (!this.categories.length) {
      this.selectedCategory = '';
      return;
    }

    const stillValid = this.categories.some((c) => c.id === this.selectedCategory);
    if (!stillValid) {
      this.selectedCategory = this.categories[0].id;
    }
  }

  private toCategoryId(label: string): string {
    return label
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'other';
  }

  private preloadActivityImages(): void {
    if (this.allActivities.length === 0) return;

    this.imagePreloadService.clearCache();
    this.imageUrlCache.clear();
    this.preloadedImageUrls.clear();

    const withImages = this.allActivities.filter((activity) => !!activity.image?.trim());
    const withoutImages = this.allActivities.filter((activity) => !activity.image?.trim());

    // Missing image → keep placeholder visible (do not mark as loaded).
    withoutImages.forEach((activity) => {
      this.imagesLoaded[activity.id] = false;
    });

    if (withImages.length === 0) {
      return;
    }

    this.imagePreloadService.preloadImages(
      withImages.map((activity) => ({ id: activity.id, image: activity.image })),
      this.imagesLoaded,
      this.cdr,
    );
  }

  isImageLoaded(activityId: string): boolean {
    return !!this.imagesLoaded[activityId];
  }

  /** Kept for template compatibility; prefer activity.image directly. */
  getImageUrl(activityId: string): string {
    return (
      this.allActivities.find((a) => a.id === activityId)?.image ||
      this.imagePreloadService.getImageUrl(activityId)
    );
  }

  get filteredActivities(): Activity[] {
    if (!this.selectedCategory) {
      return [];
    }
    return this.allActivities.filter((activity) => activity.category === this.selectedCategory);
  }

  trackByActivityId(_index: number, activity: Activity): string {
    return activity.id;
  }

  get selectedActivityIds(): string[] {
    const apiSubmittedIds = Array.from(this.activitySubmittedStatus.entries())
      .filter(([_, isSubmitted]) => isSubmitted === true)
      .map(([id]) => id);

    const allSelected = new Set(apiSubmittedIds);
    this.temporarySelections.forEach((id) => allSelected.add(id));
    this.temporaryDeselections.forEach((id) => allSelected.delete(id));

    return Array.from(allSelected);
  }

  /** True when the user has changed selection and Save should be allowed (including clear-all). */
  get canSaveActivities(): boolean {
    if (this.isSaving) {
      return false;
    }
    if (this.temporarySelections.size > 0 || this.temporaryDeselections.size > 0) {
      return true;
    }
    return this.selectedActivityIds.length > 0;
  }

  selectCategory(category: ActivityCategory): void {
    if (this.selectedCategory === category) return;
    this.selectedCategory = category;
  }

  toggleActivity(activity: Activity): void {
    const activityId = activity.id;
    const isSubmittedInAPI = this.activitySubmittedStatus.get(activityId) === true;

    if (isSubmittedInAPI) {
      if (this.temporaryDeselections.has(activityId)) {
        this.temporaryDeselections.delete(activityId);
      } else {
        this.temporaryDeselections.add(activityId);
        this.temporarySelections.delete(activityId);
      }
    } else if (this.temporarySelections.has(activityId)) {
      this.temporarySelections.delete(activityId);
    } else {
      this.temporarySelections.add(activityId);
      this.temporaryDeselections.delete(activityId);
    }
  }

  handleKeyDown(event: KeyboardEvent, activity: Activity): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleActivity(activity);
    }
  }

  isActivitySelected(activityId: string): boolean {
    if (this.temporaryDeselections.has(activityId)) {
      return false;
    }
    if (this.temporarySelections.has(activityId)) {
      return true;
    }
    return this.activitySubmittedStatus.get(activityId) === true;
  }

  onBackClick(): void {
    const previousRoute = (sessionStorage.getItem('previousRoute') || '').trim();
    if (previousRoute === '/success' || previousRoute === '/success-meal-transport') {
      sessionStorage.removeItem('previousRoute');
      this.router.navigate([previousRoute]);
      return;
    }
    this.router.navigate(['/success-meal-transport']);
  }

  onSkipClick(): void {
    this.temporarySelections.clear();
    this.temporaryDeselections.clear();
    const previousRoute = (sessionStorage.getItem('previousRoute') || '').trim();
    if (previousRoute === '/success' || previousRoute === '/success-meal-transport') {
      sessionStorage.removeItem('previousRoute');
      this.router.navigate([previousRoute]);
      return;
    }
    this.router.navigate(['/upcoming-events'], {
      state: { activitiesSelected: false },
    });
  }

  onSaveClick(): void {
    const selectedActivityIds = this.selectedActivityIds;
    const hasPendingChanges =
      this.temporarySelections.size > 0 || this.temporaryDeselections.size > 0;

    // Allow saving an empty list only when clearing previously selected activities.
    if (selectedActivityIds.length === 0 && !hasPendingChanges) {
      this.showToaster('Please select at least one activity', 'warning');
      return;
    }

    const userId = this.userDetails?.value;
    if (!userId) {
      this.showToaster('User information not found. Please check in again.', 'danger');
      return;
    }

    this.isSaving = true;
    this.error = null;

    const payload = {
      userId: Number(userId),
      activityId: selectedActivityIds,
      date: this.resolveActivityDate(),
    };

    this.activityService
      .participateInActivity(payload)
      .pipe(
        finalize(() => {
          this.isSaving = false;
        }),
      )
      .subscribe({
        next: () => {
          this.temporarySelections.clear();
          this.temporaryDeselections.clear();
          // Reflect saved state immediately so reload race doesn't flash old selections
          this.activitySubmittedStatus.clear();
          selectedActivityIds.forEach((id) => this.activitySubmittedStatus.set(id, true));
          this.showToaster('Activities saved successfully!', 'success');
          this.loadActivities();
          setTimeout(() => {
            const previousRoute = (sessionStorage.getItem('previousRoute') || '').trim();
            if (previousRoute === '/success' || previousRoute === '/success-meal-transport') {
              sessionStorage.removeItem('previousRoute');
              this.router.navigate([previousRoute], {
                state: { activitiesSelected: selectedActivityIds.length > 0 },
              });
              return;
            }
            this.router.navigate(['/upcoming-events'], {
              state: { activitiesSelected: selectedActivityIds.length > 0 },
            });
          }, 500);
        },
        error: (err) => {
          const errorMessage = err?.error?.message ?? 'Failed to save activities. Please try again.';
          this.showToaster(errorMessage, 'danger');
        },
      });
  }

  private showToaster(
    message: string,
    variant: "success" | "danger" | "warning" | "info" = "info",
    duration = 4000,
  ): void {
    this.toasterMessage = message;
    this.toasterVariant = variant;
    if (this.toasterTimeoutId !== null) {
      clearTimeout(this.toasterTimeoutId);
    }
    this.toasterTimeoutId = setTimeout(() => {
      this.dismissToaster();
    }, duration);
  }

  dismissToaster(): void {
    this.toasterMessage = null;
    if (this.toasterTimeoutId !== null) {
      clearTimeout(this.toasterTimeoutId);
    }
  }
}
